import { DEFAULT_CONSOLE_URL } from "./consoleProxyTarget";

type RuntimeConfig = {
  BASE_API_URL?: string;
  STORAGE_API_URL?: string;
  AUTH_MODE?: string;
  CONSOLE_URL?: string;
  CONSOLE_API_URL?: string;
  CONSOLE_SCOPED_INSTANCE?: string;
  CONSOLE_ORGANIZATION_ID?: string;
  FIXED_ORGANIZATION_ID?: string;
  AUTH_HANKO_API_URL?: string;
  INSTANCE_ID?: string;
  INSTANCE_ORGANIZATION_ID?: string;
  LIVEKIT_TARGETS?: string;
  OPENUI_RUNTIME_ENABLED?: string;
  CHAT_TRANSPORT?: string;
};

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

const RUNTIME_SCRIPT_ID = "agent-os-runtime-config";

function isPlaceholder(value: string | undefined | null): boolean {
  if (!value) return true;
  return value.startsWith("${") && value.endsWith("}");
}

function hasRuntimeValue(value: string | undefined | null): value is string {
  return value != null && !isPlaceholder(value) && value.trim().length > 0;
}

function normalizeDirectApiUrl(url: string): string {
  const trimmed = url.replace(/\/+$/, "");

  if (!trimmed) return "/api/v1";
  if (trimmed === "/api") return "/api/v1";
  if (trimmed.endsWith("/api/v1") || trimmed.endsWith("/v1")) return trimmed;
  if (trimmed.endsWith("/api")) return `${trimmed}/v1`;

  return `${trimmed}/api/v1`;
}

export function getBaseApiUrl(): string {
  const runtimeUrl = window.__RUNTIME_CONFIG__?.BASE_API_URL;

  if (!isPlaceholder(runtimeUrl)) {
    return normalizeDirectApiUrl(runtimeUrl!);
  }

  const buildTimeUrl =
    (import.meta.env.VITE_BASE_API_URL as string | undefined) ||
    (import.meta.env.VITE_API_BASE_URL as string | undefined);

  if (!isPlaceholder(buildTimeUrl)) {
    return normalizeDirectApiUrl(buildTimeUrl!);
  }

  return "/api/v1";
}

/**
 * Absolute root of the AgentMaurice API, without its `/api` or `/api/vN`
 * suffix (scheme, host and any path prefix): the base of the public URLs the
 * OS shows to a caller (webhook triggers, Application and Agent curl
 * examples). A relative API base (same-origin deployment, Vite proxy)
 * resolves against the page origin (AGENT-833).
 */
export function getApiRootUrl(): string {
  const base = getBaseApiUrl().replace(/\/+$/, "") || "/api/v1";
  const absolute =
    typeof window !== "undefined" && window.location?.href
      ? new URL(base, window.location.href).toString()
      : base;
  return absolute.replace(/\/+$/, "").replace(/\/api(?:\/v\d+)?$/i, "");
}

function normalizeStorageApiUrl(url: string): string {
  const trimmed = url.replace(/\/+$/, "");

  if (!trimmed) return "";
  const stripSuffix = (suffix: string) => {
    const base = trimmed.slice(0, -suffix.length);
    if (base) return base;
    if (typeof window !== "undefined" && window.location?.origin) {
      return window.location.origin;
    }
    return "";
  };
  if (trimmed.endsWith("/storage")) return stripSuffix("/storage");
  if (trimmed.endsWith("/api/v1")) return stripSuffix("/api/v1");
  if (trimmed.endsWith("/api")) return stripSuffix("/api");

  return trimmed;
}

export function getStorageApiUrl(): string {
  const runtimeUrl = window.__RUNTIME_CONFIG__?.STORAGE_API_URL;

  if (!isPlaceholder(runtimeUrl)) {
    return normalizeStorageApiUrl(runtimeUrl!);
  }

  const buildTimeUrl =
    (import.meta.env.VITE_STORAGE_API_URL as string | undefined) ||
    (import.meta.env.VITE_STORAGE_BASE_URL as string | undefined);

  if (!isPlaceholder(buildTimeUrl)) {
    return normalizeStorageApiUrl(buildTimeUrl!);
  }

  const baseApiUrl = getBaseApiUrl();
  if (baseApiUrl === "/api") {
    return "";
  }

  return normalizeStorageApiUrl(baseApiUrl);
}

export type HankoConfig = {
  apiUrl: string;
};

const DEFAULT_HANKO_API_URL = "https://auth.agentmaurice.app";

export type AuthMode = "local" | "hanko";

function normalizeAuthMode(value: string | undefined): AuthMode {
  const normalized = value?.trim().toLowerCase();
  if (normalized === "local" || normalized === "dev") {
    return "local";
  }
  return "hanko";
}

export function getHankoConfig(): HankoConfig {
  const runtimeApiUrl = window.__RUNTIME_CONFIG__?.AUTH_HANKO_API_URL;

  const apiUrl = !isPlaceholder(runtimeApiUrl)
    ? runtimeApiUrl!
    : (import.meta.env.VITE_AUTH_HANKO_API_URL as string | undefined) ||
      DEFAULT_HANKO_API_URL;

  return { apiUrl: apiUrl.trim().replace(/\/+$/, "") };
}

export function getAuthMode(): AuthMode {
  const runtimeMode = window.__RUNTIME_CONFIG__?.AUTH_MODE;

  if (!isPlaceholder(runtimeMode)) {
    return normalizeAuthMode(runtimeMode);
  }

  return normalizeAuthMode(
    import.meta.env.VITE_AUTH_MODE as string | undefined,
  );
}

export function getConsoleUrl(): string {
  const runtimeUrl = window.__RUNTIME_CONFIG__?.CONSOLE_URL;
  if (!isPlaceholder(runtimeUrl) && runtimeUrl?.trim()) {
    return runtimeUrl.trim().replace(/\/+$/, "");
  }

  const buildTimeUrl = import.meta.env.VITE_CONSOLE_URL as string | undefined;
  if (!isPlaceholder(buildTimeUrl) && buildTimeUrl?.trim()) {
    return buildTimeUrl.trim().replace(/\/+$/, "");
  }

  return DEFAULT_CONSOLE_URL;
}

function isAbsoluteHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function isLocalHostname(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

function isLocalBrowserOrigin(): boolean {
  if (typeof window === "undefined") return false;
  return isLocalHostname(window.location?.hostname);
}

function isHostedConsoleUrl(url: string): boolean {
  if (!isAbsoluteHttpUrl(url)) return false;
  try {
    return !isLocalHostname(new URL(url).hostname.toLowerCase());
  } catch {
    return false;
  }
}

/** Local OS + hosted Console: resolve the user's Console org, not the chatserver org. */
export function shouldResolveConsoleOrganization(): boolean {
  if (isConsoleScopedInstance()) return true;
  return isHostedConsoleUrl(getConsoleUrl());
}

export function getConsoleApiUrl(): string {
  const runtimeUrl = window.__RUNTIME_CONFIG__?.CONSOLE_API_URL;
  if (!isPlaceholder(runtimeUrl) && runtimeUrl?.trim()) {
    return runtimeUrl.trim().replace(/\/+$/, "");
  }

  const buildTimeUrl = import.meta.env.VITE_CONSOLE_API_URL as
    | string
    | undefined;
  if (!isPlaceholder(buildTimeUrl) && buildTimeUrl?.trim()) {
    return buildTimeUrl.trim().replace(/\/+$/, "");
  }

  const consoleUrl = getConsoleUrl();
  if (
    import.meta.env.DEV &&
    isLocalBrowserOrigin() &&
    isAbsoluteHttpUrl(consoleUrl)
  ) {
    return "/console-api";
  }

  return consoleUrl;
}

export function getRuntimeInstanceId(): string | null {
  const runtimeInstanceId = window.__RUNTIME_CONFIG__?.INSTANCE_ID;
  if (!isPlaceholder(runtimeInstanceId)) {
    return runtimeInstanceId!.trim() || null;
  }

  const buildTimeInstanceId = import.meta.env.VITE_INSTANCE_ID as
    | string
    | undefined;
  if (!isPlaceholder(buildTimeInstanceId)) {
    return buildTimeInstanceId!.trim() || null;
  }

  return null;
}

export function getFixedOrganizationId(): string | null {
  const runtimeOrgId = window.__RUNTIME_CONFIG__?.FIXED_ORGANIZATION_ID;
  if (!isPlaceholder(runtimeOrgId)) {
    return runtimeOrgId!.trim() || null;
  }

  const buildTimeOrgId = import.meta.env.VITE_FIXED_ORGANIZATION_ID as
    | string
    | undefined;
  if (!isPlaceholder(buildTimeOrgId)) {
    return buildTimeOrgId!.trim() || null;
  }

  return null;
}

export function getConsoleOrganizationId(): string | null {
  const runtimeOrgId = window.__RUNTIME_CONFIG__?.CONSOLE_ORGANIZATION_ID;
  if (!isPlaceholder(runtimeOrgId)) {
    return runtimeOrgId!.trim() || null;
  }

  const buildTimeOrgId = import.meta.env.VITE_CONSOLE_ORGANIZATION_ID as
    | string
    | undefined;
  if (!isPlaceholder(buildTimeOrgId)) {
    return buildTimeOrgId!.trim() || null;
  }

  return null;
}

export function getLiveKitTargets(): string[] {
  const runtimeTargets = window.__RUNTIME_CONFIG__?.LIVEKIT_TARGETS;
  const buildTargets =
    (import.meta.env.VITE_LIVEKIT_TARGETS as string | undefined) ||
    "gamemaster";

  const value = !isPlaceholder(runtimeTargets) ? runtimeTargets! : buildTargets;

  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export type ChatTransport = "livekit" | "websocket";

/**
 * Text chat transport of Run and of the Studio thread (AGENT-792,
 * wiki architecture/os-run-chat-websocket). Since AGENT-840 the Fiber
 * WebSocket channel is the default; `livekit` keeps the former text path for
 * an API that does not serve the channel. Voice always stays on LiveKit.
 */
export function getChatTransport(): ChatTransport {
  const runtimeValue = window.__RUNTIME_CONFIG__?.CHAT_TRANSPORT;
  const value = hasRuntimeValue(runtimeValue)
    ? runtimeValue
    : (import.meta.env.VITE_CHAT_TRANSPORT as string | undefined);
  return value?.trim().toLowerCase() === "livekit" ? "livekit" : "websocket";
}

function normalizeBooleanFlag(
  value: string | undefined,
  fallback: boolean,
): boolean {
  if (value == null || isPlaceholder(value)) {
    return fallback;
  }
  switch (value.trim().toLowerCase()) {
    case "1":
    case "true":
    case "yes":
    case "on":
      return true;
    case "0":
    case "false":
    case "no":
    case "off":
      return false;
    default:
      return fallback;
  }
}

export function isOpenUIRuntimeEnabled(): boolean {
  const runtimeValue = window.__RUNTIME_CONFIG__?.OPENUI_RUNTIME_ENABLED;
  if (runtimeValue != null && !isPlaceholder(runtimeValue)) {
    return normalizeBooleanFlag(runtimeValue, true);
  }

  return normalizeBooleanFlag(
    import.meta.env.VITE_OPENUI_RUNTIME_ENABLED as string | undefined,
    true,
  );
}

/** OpenUI Inspect is opt-in because its floating layer can intercept app UI. */
export function isOpenUIDevtoolsEnabled(): boolean {
  return import.meta.env.DEV &&
    normalizeBooleanFlag(
      import.meta.env.VITE_OPENUI_DEVTOOLS as string | undefined,
      false,
    );
}

export function isConsoleScopedInstance(): boolean {
  const runtimeValue = window.__RUNTIME_CONFIG__?.CONSOLE_SCOPED_INSTANCE;
  if (runtimeValue != null && !isPlaceholder(runtimeValue)) {
    return normalizeBooleanFlag(runtimeValue, false);
  }

  return normalizeBooleanFlag(
    import.meta.env.VITE_CONSOLE_SCOPED_INSTANCE as string | undefined,
    false,
  );
}

let runtimeConfigPromise: Promise<void> | null = null;

function runtimeConfigNeedsTauriFallback(config?: RuntimeConfig): boolean {
  if (!config) {
    return true;
  }

  return !(
    hasRuntimeValue(config.BASE_API_URL) &&
    hasRuntimeValue(config.STORAGE_API_URL) &&
    hasRuntimeValue(config.AUTH_MODE)
  );
}

function mergeRuntimeConfig(
  current: RuntimeConfig | undefined,
  fallback: RuntimeConfig | null,
): RuntimeConfig | undefined {
  if (!fallback) {
    return current;
  }

  const merged: RuntimeConfig = { ...(current || {}) };

  for (const [key, value] of Object.entries(fallback)) {
    if (hasRuntimeValue(value)) {
      merged[key as keyof RuntimeConfig] = value;
    }
  }

  return merged;
}

async function loadRuntimeScriptConfig(): Promise<void> {
  const existing = document.getElementById(
    RUNTIME_SCRIPT_ID,
  ) as HTMLScriptElement | null;

  if (existing) {
    await new Promise<void>((resolve) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => resolve(), { once: true });
    });
    return;
  }

  await new Promise<void>((resolve) => {
    const script = document.createElement("script");
    script.id = RUNTIME_SCRIPT_ID;
    const separator = import.meta.env.BASE_URL.includes("?") ? "&" : "?";
    script.src = `${import.meta.env.BASE_URL}config.js${separator}ts=${Date.now()}`;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

export function loadRuntimeConfig(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (runtimeConfigPromise) {
    return runtimeConfigPromise;
  }

  runtimeConfigPromise = (async () => {
    await loadRuntimeScriptConfig();

    if (!runtimeConfigNeedsTauriFallback(window.__RUNTIME_CONFIG__)) {
      return;
    }

    const { getDesktopRuntimeConfig } =
      await import("@/integrations/tauri/runtimeConfig");
    const desktopRuntimeConfig = await getDesktopRuntimeConfig();
    const mergedConfig = mergeRuntimeConfig(
      window.__RUNTIME_CONFIG__,
      desktopRuntimeConfig,
    );
    if (mergedConfig) {
      window.__RUNTIME_CONFIG__ = mergedConfig;
    }
  })().catch(() => undefined);

  return runtimeConfigPromise;
}
